import Icon, { IconName } from '@features/ui-atoms/components/Icon';
import { Input as InputJoy, inputClasses as inputJoyClasses, styled } from '@mui/joy';
import type { SxProps } from '@mui/joy/styles/types';
import { ChangeEvent, FocusEvent, ReactNode, KeyboardEvent } from 'react';

export interface InputProps {
  id?: string;
  name?: string;
  type?: 'text' | 'number' | 'date' | 'password' | 'email';
  /** `decimal` for a number bound with `decimal()`: a text input with the numeric keyboard on touch devices. */
  inputMode?: 'decimal' | 'numeric' | 'text';
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  startDecorator?: ReactNode;
  endDecorator?: ReactNode;
  autoFocus?: boolean;
  sx?: SxProps;
  withSearchIcon?: boolean;
  autoComplete?: string;
}

const Input = ({
  type = 'text',
  inputMode,
  startDecorator,
  withSearchIcon,
  error,
  autoComplete = 'off',
  ...rest
}: InputProps) => (
  <InputStyled
    type={type}
    error={error}
    color={error ? 'danger' : 'neutral'}
    autoComplete={autoComplete}
    slotProps={inputMode === undefined ? undefined : { input: { inputMode } }}
    {...rest}
    startDecorator={startDecorator || (withSearchIcon && <Icon name={IconName.search} />)}
  />
);

export default Input;

export const InputStyled = styled(InputJoy)<{ error?: boolean; width?: string; endDecoratorWidth?: string }>(
  ({ theme, width, endDecoratorWidth, error }) => ({
    borderRadius: '2px',
    background: theme.vars.palette.grey1,
    height: '36px',
    padding: '8px 12px',
    color: theme.vars.palette.black,
    fontSize: '14px',
    fontWeight: 400,
    boxShadow: 'none',
    '--Input-focusedThickness': '0',
    width: width ?? '100%',
    border: error ? `1px solid ${theme.vars.palette.orange1}` : '1px solid transparent',

    '&:hover:not(.Mui-error):not(.Mui-disabled)': {
      borderColor: theme.vars.palette.grey2,
    },

    '&:focus': {
      outline: 'none',
      borderColor: 'transparent',
    },

    '&.Mui-error': {
      border: `1px solid ${theme.vars.palette.orange1}`,
    },

    '& svg': {
      color: theme.vars.palette.grey4,
    },

    [`& .${inputJoyClasses.input}::placeholder`]: {
      color: theme.vars.palette.grey4,
      opacity: 1,
    },

    '& .rmdp-input::placeholder': {
      color: theme.vars.palette.grey4,
      opacity: 1,
    },

    '&::placeholder': {
      color: theme.vars.palette.grey4,
    },

    [`&.${inputJoyClasses.disabled}`]: {
      background: theme.vars.palette.grey2,
      color: theme.vars.palette.grey4,
      cursor: 'not-allowed !important',
      pointerEvents: 'all !important',
    },

    [`&.${inputJoyClasses.disabled} .${inputJoyClasses.input}`]: {
      cursor: 'not-allowed !important',
      pointerEvents: 'all !important',
    },

    [`.${inputJoyClasses.startDecorator}`]: {
      fontSize: theme.fontSize.sm,
      color: theme.vars.palette.grey3,
      '& div': {
        height: '20px',
        color: theme.vars.palette.grey3,
      },
    },

    [`.${inputJoyClasses.endDecorator}`]: {
      fontSize: theme.fontSize.sm,
      color: theme.vars.palette.grey3,
      '& div': {
        height: '20px',
        color: theme.vars.palette.grey3,
      },
    },

    '& ::-webkit-calendar-picker-indicator': {
      display: 'none',
    },

    '.rmdp-wrapper': {
      backgroundColor: theme.vars.palette.background.body,
    },

    '.rmdp-container': {
      width: `calc(${width ?? '190px'} - 2*12px - ${endDecoratorWidth ?? '20px'})`,
    },

    '.rmdp-input': {
      border: 'none',
      padding: '0 0 4px 0',
      background: 'inherit',
      fontFamily: 'inherit',
      color: theme.vars.palette.black,
    },

    '.rmdp-input:focus': {
      outline: 'none',
      boxShadow: 'none',
      border: 'none',
    },
  }),
);
